<template>
  <div class="media-library-page">
    <b-card no-body class="media-library-card">
      <div class="media-library-header">
        <div class="media-library-hero">
          <div class="media-library-hero__icon">
            <feather-icon icon="ImageIcon" size="20" />
          </div>
          <div>
            <h2 class="mb-25">Media Library</h2>
            <p class="text-muted mb-0">
              Browse, search, upload, and manage reusable media across products, sliders, categories, and library uploads.
            </p>
          </div>
        </div>

        <div class="media-library-actions">
          <b-button
            variant="primary"
            class="media-btn media-btn--primary"
            @click="$refs.fileInput.click()"
          >
            <feather-icon icon="UploadCloudIcon" size="16" class="mr-50" />
            <span>Upload Media</span>
          </b-button>

          <input
            ref="fileInput"
            type="file"
            class="d-none"
            multiple
            @change="handleFileInput"
          />

          <b-button
            variant="outline-secondary"
            class="media-btn media-btn--ghost"
            @click="fetchLibrary"
          >
            <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
            <span>Refresh</span>
          </b-button>
        </div>
      </div>

      <div class="media-toolbar-card">
        <div class="media-library-toolbar">
          <div class="media-library-toolbar__left media-library-toolbar__left--inline">
            <b-input-group class="media-search-box media-control">
              <template #prepend>
                <b-input-group-text class="media-control__prepend">
                  <feather-icon icon="SearchIcon" size="15" />
                </b-input-group-text>
              </template>
              <b-form-input
                v-model.trim="search"
                placeholder="Live search by image/file name..."
                @input="onSearchInput"
              />
            </b-input-group>

            <b-form-select
              v-model="source"
              :options="sourceOptions"
              class="media-filter-select media-control"
              @change="onFilterChange"
            />

            <b-form-select
              v-model="type"
              :options="typeOptions"
              class="media-filter-select media-control"
              @change="onFilterChange"
            />

            <b-form-select
              v-model="collection"
              :options="collectionOptions"
              class="media-filter-select media-filter-select--wide media-control"
              @change="onFilterChange"
            />

            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              class="media-filter-select media-control"
              @change="onFilterChange"
            />
          </div>

          <div class="media-library-toolbar__right media-library-toolbar__right--inline">
            <div class="media-summary-pill">
              <feather-icon icon="LayersIcon" size="14" class="mr-50" />
              <span v-if="pagination.total">
                {{ pagination.from || 0 }}-{{ pagination.to || 0 }} of {{ pagination.total }}
              </span>
              <span v-else>0 items</span>
            </div>
          </div>
        </div>

        <div class="media-stat-chips">
          <span class="media-chip media-chip--primary">Source: {{ sourceLabel }}</span>
          <span class="media-chip media-chip--secondary">Type: {{ typeLabel }}</span>
          <span class="media-chip media-chip--info">Collection: {{ collectionLabel }}</span>
          <span class="media-chip media-chip--success">Per page: {{ perPage }}</span>
          <span class="media-chip media-chip--warning">Selected: {{ selectedIds.length }}</span>
        </div>
      </div>

      <div class="media-bulk-bar" v-if="selectedIds.length">
        <div class="media-bulk-bar__left">
          <b-form-checkbox
            :checked="allVisibleSelected"
            @change="toggleSelectAllVisible"
            switch
          >
            Select all visible
          </b-form-checkbox>
          <span class="text-muted">{{ selectedIds.length }} item(s) selected</span>
        </div>

        <div class="media-bulk-bar__right">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="media-btn media-btn--ghost"
            @click="clearSelection"
          >
            Clear Selection
          </b-button>

          <b-button
            variant="outline-danger"
            size="sm"
            class="media-btn media-btn--danger"
            :disabled="!canBulkDelete || isBulkDeleting"
            @click="bulkDeleteSelected"
          >
            <b-spinner v-if="isBulkDeleting" small class="mr-50" />
            Bulk Delete
          </b-button>
        </div>
      </div>

      <div v-if="uploadQueue.length" class="media-upload-panel">
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h5 class="mb-50">Ready to Upload</h5>
            <small class="text-muted">{{ uploadQueue.length }} file(s) selected</small>
          </div>

          <div class="d-flex align-items-center mt-1 mt-md-0">
            <b-button
              variant="primary"
              size="sm"
              class="media-btn media-btn--primary"
              :disabled="isUploading"
              @click="uploadSelectedFiles"
            >
              <b-spinner v-if="isUploading" small class="mr-50" />
              {{ isUploading ? 'Uploading...' : 'Upload Now' }}
            </b-button>

            <b-button
              variant="outline-secondary"
              size="sm"
              class="media-btn media-btn--ghost ml-1"
              :disabled="isUploading"
              @click="clearUploadQueue"
            >
              Clear
            </b-button>
          </div>
        </div>

        <div class="upload-queue-list mt-1">
          <div
            v-for="(file, index) in uploadQueue"
            :key="`${file.name}-${index}`"
            class="upload-queue-list__item"
          >
            <div class="d-flex align-items-center min-width-0">
              <div class="upload-queue-list__icon">
                <feather-icon icon="FileIcon" size="14" />
              </div>
              <span class="text-truncate">{{ file.name }}</span>
            </div>
            <small class="text-muted">{{ formatFileSize(file.size) }}</small>
          </div>
        </div>
      </div>

      
      <div
        v-if="activeItem"
        class="media-mobile-selection-bar"
      >
        <div class="media-mobile-selection-bar__left">
          <div class="media-mobile-selection-bar__thumb">
            <img
              v-if="isImage(activeItem)"
              :src="activeItem.preview_url || activeItem.original_url"
              :alt="activeItem.file_name || activeItem.name"
              @error="handleImageError"
            />
            <div v-else class="media-mobile-selection-bar__thumb-fallback">
              <feather-icon icon="FileTextIcon" size="14" />
            </div>
          </div>
          <div class="media-mobile-selection-bar__meta">
            <div class="media-mobile-selection-bar__name">
              {{ activeItem.file_name || activeItem.name || 'Selected item' }}
            </div>
            <small class="text-muted">{{ activeItem.collection_name || 'media' }}</small>
          </div>
        </div>

        <b-button
          variant="primary"
          size="sm"
          class="media-btn media-btn--primary media-mobile-selection-bar__button"
          @click="mobileDetailsOpen = true"
        >
          Details
        </b-button>
      </div>

      <div class="media-mobile-backdrop" v-if="mobileDetailsOpen" @click="mobileDetailsOpen = false"></div>

      <div
        class="media-mobile-drawer"
        :class="{ 'is-open': mobileDetailsOpen }"
      >
        <div class="media-mobile-drawer__handle"></div>
        <div class="media-mobile-drawer__header">
          <h5 class="mb-0">Attachment Details</h5>
          <b-button
            variant="flat-secondary"
            class="btn-icon"
            @click="mobileDetailsOpen = false"
          >
            <feather-icon icon="XIcon" size="18" />
          </b-button>
        </div>

        <div class="media-mobile-drawer__body" v-if="activeItem">
          <div class="media-sidebar-preview mb-1">
            <img
              v-if="isImage(activeItem)"
              :src="activeItem.original_url || activeItem.preview_url"
              :alt="activeItem.file_name || activeItem.name"
              @error="handleImageError"
            />
            <div v-else class="media-sidebar-preview__fallback">
              <feather-icon icon="FileTextIcon" size="30" />
            </div>
          </div>

          <ul class="list-unstyled media-sidebar-meta mb-1">
            <li><strong>File:</strong><span>{{ activeItem.file_name || activeItem.name || '--' }}</span></li>
            <li><strong>Type:</strong><span>{{ activeItem.mime_type || '--' }}</span></li>
            <li><strong>Source:</strong><span>{{ activeItem.model_type || '--' }}</span></li>
            <li><strong>Collection:</strong><span>{{ activeItem.collection_name || '--' }}</span></li>
            <li><strong>Size:</strong><span>{{ formatFileSize(activeItem.size || 0) }}</span></li>
            <li><strong>ID:</strong><span>{{ activeItem.id }}</span></li>
          </ul>

          <b-form-group label="Name" label-for="media-name-mobile">
            <b-form-input
              id="media-name-mobile"
              v-model="editForm.name"
              placeholder="Media name"
              class="media-control"
            />
          </b-form-group>

          <b-form-group label="Alt text" label-for="media-alt-mobile">
            <b-form-textarea
              id="media-alt-mobile"
              v-model="editForm.alt"
              rows="2"
              placeholder="Alt text"
              class="media-control"
            />
          </b-form-group>

          <b-form-group label="Caption" label-for="media-caption-mobile">
            <b-form-textarea
              id="media-caption-mobile"
              v-model="editForm.caption"
              rows="2"
              placeholder="Caption"
              class="media-control"
            />
          </b-form-group>

          <div class="media-sidebar-actions">
            <b-button
              variant="primary"
              size="sm"
              class="media-btn media-btn--primary"
              :disabled="isSaving"
              @click="saveMetadata"
            >
              <b-spinner v-if="isSaving" small class="mr-50" />
              {{ isSaving ? 'Saving...' : 'Save Details' }}
            </b-button>

            <b-button
              variant="outline-secondary"
              size="sm"
              class="media-btn media-btn--ghost"
              @click="copyUrl(activeItem)"
            >
              Copy URL
            </b-button>

            <b-button
              variant="outline-secondary"
              size="sm"
              class="media-btn media-btn--ghost"
              @click="openOriginal(activeItem)"
            >
              Open
            </b-button>
          </div>

          <div class="mt-2">
            <b-button
              variant="outline-danger"
              size="sm"
              class="media-btn media-btn--danger"
              :disabled="!canDeleteActive || isDeleting"
              @click="removeMedia"
            >
              <b-spinner v-if="isDeleting" small class="mr-50" />
              Delete
            </b-button>

            <small
              v-if="!canDeleteActive"
              class="d-block text-muted mt-50"
            >
              Only library uploads can be deleted here.
            </small>
          </div>
        </div>
      </div>

      <div class="media-library-content">
        <div class="media-library-main">
          <div
            class="media-dropzone mb-2"
            :class="{ 'is-dragging': isDragging }"
            @dragenter.prevent="isDragging = true"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <div class="media-dropzone__inner">
              <div class="media-dropzone__icon">
                <feather-icon icon="ImageIcon" size="24" />
              </div>
              <div class="font-weight-bolder">Drop files here to upload</div>
              <small class="text-muted">or use the Upload Media button above</small>
            </div>
          </div>

          <div v-if="isLoading" class="media-state media-state--loading">
            <b-spinner variant="primary" />
            <p class="text-muted mt-1 mb-0">Loading media...</p>
          </div>

          <div v-else-if="mediaItems.length" class="media-grid">
            <div
              v-for="item in mediaItems"
              :key="item.id"
              class="media-grid__item"
              :class="{
                'is-active': activeItem && activeItem.id === item.id,
                'is-selected': isSelected(item.id),
              }"
              @click="selectItem(item)"
            >
              <div class="media-grid__thumb">
                <img
                  v-if="isImage(item)"
                  :src="item.preview_url || item.original_url"
                  :alt="item.file_name || item.name"
                  loading="lazy"
                  @error="handleImageError"
                />

                <div v-else class="media-grid__fallback">
                  <feather-icon icon="FileTextIcon" size="26" />
                </div>

                <span class="media-grid__badge">
                  {{ item.collection_name || 'media' }}
                </span>

                <label class="media-grid__checkbox" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(item.id)"
                    @change="toggleSelection(item)"
                  />
                  <span></span>
                </label>
              </div>

              <div class="media-grid__meta">
                <div class="media-grid__name">
                  {{ item.file_name || item.name || 'Untitled' }}
                </div>
                <div class="media-grid__sub">
                  {{ humanMime(item.mime_type) }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="media-state media-state--empty">
            <div class="media-empty-icon">
              <feather-icon icon="ImageIcon" size="34" />
            </div>
            <h5 class="mb-50 mt-1">No media found</h5>
            <p class="text-muted mb-0">
              Try changing filters or upload new media.
            </p>
          </div>

          <div
            v-if="pagination.total > pagination.per_page"
            class="media-pagination"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="pagination.per_page"
              first-number
              last-number
              align="center"
              @input="onPageChange"
            />
          </div>
        </div>

        <div class="media-library-sidebar">
          <b-card no-body class="media-sidebar-card">
            <div class="media-sidebar-card__header">
              <h5 class="mb-0">Attachment Details</h5>
            </div>

            <div class="media-sidebar-card__body">
              <div v-if="activeItem">
                <div class="media-sidebar-preview mb-1">
                  <img
                    v-if="isImage(activeItem)"
                    :src="activeItem.original_url || activeItem.preview_url"
                    :alt="activeItem.file_name || activeItem.name"
                    @error="handleImageError"
                  />
                  <div v-else class="media-sidebar-preview__fallback">
                    <feather-icon icon="FileTextIcon" size="30" />
                  </div>
                </div>

                <ul class="list-unstyled media-sidebar-meta mb-1">
                  <li><strong>File:</strong><span>{{ activeItem.file_name || activeItem.name || '--' }}</span></li>
                  <li><strong>Type:</strong><span>{{ activeItem.mime_type || '--' }}</span></li>
                  <li><strong>Source:</strong><span>{{ activeItem.model_type || '--' }}</span></li>
                  <li><strong>Collection:</strong><span>{{ activeItem.collection_name || '--' }}</span></li>
                  <li><strong>Size:</strong><span>{{ formatFileSize(activeItem.size || 0) }}</span></li>
                  <li><strong>ID:</strong><span>{{ activeItem.id }}</span></li>
                </ul>

                <b-form-group label="Name" label-for="media-name">
                  <b-form-input
                    id="media-name"
                    v-model="editForm.name"
                    placeholder="Media name"
                    class="media-control"
                  />
                </b-form-group>

                <b-form-group label="Alt text" label-for="media-alt">
                  <b-form-textarea
                    id="media-alt"
                    v-model="editForm.alt"
                    rows="2"
                    placeholder="Alt text"
                    class="media-control"
                  />
                </b-form-group>

                <b-form-group label="Caption" label-for="media-caption">
                  <b-form-textarea
                    id="media-caption"
                    v-model="editForm.caption"
                    rows="2"
                    placeholder="Caption"
                    class="media-control"
                  />
                </b-form-group>

                <div class="media-sidebar-actions">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="media-btn media-btn--primary"
                    :disabled="isSaving"
                    @click="saveMetadata"
                  >
                    <b-spinner v-if="isSaving" small class="mr-50" />
                    {{ isSaving ? 'Saving...' : 'Save Details' }}
                  </b-button>

                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="media-btn media-btn--ghost"
                    @click="copyUrl(activeItem)"
                  >
                    Copy URL
                  </b-button>

                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="media-btn media-btn--ghost"
                    @click="openOriginal(activeItem)"
                  >
                    Open
                  </b-button>
                </div>

                <div class="mt-2">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="media-btn media-btn--danger"
                    :disabled="!canDeleteActive || isDeleting"
                    @click="removeMedia"
                  >
                    <b-spinner v-if="isDeleting" small class="mr-50" />
                    Delete
                  </b-button>

                  <small
                    v-if="!canDeleteActive"
                    class="d-block text-muted mt-50"
                  >
                    Only library uploads can be deleted here.
                  </small>
                </div>
              </div>

              <div v-else class="text-muted">
                Select any media item to preview and edit details.
              </div>
            </div>
          </b-card>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { fetchMedia, uploadMedia, updateMedia, deleteMedia } from '@/api/media'

let searchDebounce = null

export default {
  name: 'MediaLibraryIndex',

  data() {
    return {
      isLoading: false,
      isUploading: false,
      isSaving: false,
      isDeleting: false,
      isBulkDeleting: false,
      isDragging: false,
      mediaItems: [],
      activeItem: null,
      uploadQueue: [],
      search: '',
      source: 'all',
      type: 'image',
      collection: '',
      perPage: 48,
      currentPage: 1,
      pagination: {
        total: 0,
        per_page: 48,
        current_page: 1,
        from: 0,
        to: 0,
      },
      editForm: {
        name: '',
        alt: '',
        caption: '',
      },
      selectedIds: [],
      mobileDetailsOpen: false,
      sourceOptions: [
        { value: 'all', text: 'All Sources' },
        { value: 'library', text: 'Library Uploads' },
        { value: 'product', text: 'Product Images' },
      ],
      typeOptions: [
        { value: '', text: 'All Types' },
        { value: 'image', text: 'Images' },
        { value: 'video', text: 'Videos' },
        { value: 'document', text: 'Documents' },
      ],
      collectionOptions: [
        { value: '', text: 'All Collections' },
        { value: 'library', text: 'Library' },
        { value: 'large_pictures', text: 'Product Images' },
        { value: 'slider_image', text: 'Slider Images' },
        { value: 'category_image', text: 'Category Images' },
        { value: 'sub_category_image', text: 'Sub Category Images' },
        { value: 'child_category_image', text: 'Child Category Images' },
      ],
      perPageOptions: [
        { value: 24, text: '24 / page' },
        { value: 48, text: '48 / page' },
        { value: 96, text: '96 / page' },
      ],
    }
  },

  computed: {
    canDeleteActive() {
      return this.activeItem && this.activeItem.model_type === 'App\\Models\\MediaLibrary'
    },
    canBulkDelete() {
      return this.selectedLibraryIds.length > 0
    },
    selectedLibraryIds() {
      const visibleMap = new Map(this.mediaItems.map(item => [item.id, item]))
      return this.selectedIds.filter(id => {
        const item = visibleMap.get(id)
        return item && item.model_type === 'App\\Models\\MediaLibrary'
      })
    },
    allVisibleSelected() {
      if (!this.mediaItems.length) return false
      return this.mediaItems.every(item => this.selectedIds.includes(item.id))
    },
    sourceLabel() {
      const match = this.sourceOptions.find(item => item.value === this.source)
      return match ? match.text : 'All Sources'
    },
    typeLabel() {
      const match = this.typeOptions.find(item => item.value === this.type)
      return match ? match.text : 'All Types'
    },
    collectionLabel() {
      const match = this.collectionOptions.find(item => item.value === this.collection)
      return match ? match.text : 'All Collections'
    },
  },

  mounted() {
    this.fetchLibrary()
  },

  methods: {
    async fetchLibrary() {
      this.isLoading = true

      try {
        const response = await fetchMedia({
          q: this.search || '',
          type: this.type || '',
          source: this.source || 'all',
          collection: this.collection || '',
          page: this.currentPage,
          show: this.perPage,
          sort: 'desc',
        })

        const payload = response?.data || {}
        const data = payload?.data || []
        const meta = payload?.meta?.pagination || payload?.meta || {}

        this.mediaItems = Array.isArray(data) ? data : []
        this.pagination = {
          total: Number(meta.total || this.mediaItems.length || 0),
          per_page: Number(meta.per_page || this.perPage),
          current_page: Number(meta.current_page || this.currentPage),
          from: Number(meta.from || 0),
          to: Number(meta.to || 0),
        }

        if (this.mediaItems.length) {
          const stillExists = this.activeItem
            ? this.mediaItems.find(item => item.id === this.activeItem.id)
            : null

          this.selectItem(stillExists || this.mediaItems[0], false)
        } else {
          this.activeItem = null
          this.mobileDetailsOpen = false
          this.resetEditForm()
        }
      } catch (error) {
        this.mediaItems = []
        this.activeItem = null
        this.resetEditForm()
        this.notifyError(error, 'Could not load media library.')
      } finally {
        this.isLoading = false
      }
    },

    onSearchInput() {
      clearTimeout(searchDebounce)
      searchDebounce = setTimeout(() => {
        this.currentPage = 1
        this.fetchLibrary()
      }, 300)
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchLibrary()
    },

    onPageChange(page) {
      this.currentPage = page
      this.fetchLibrary()
    },

    selectItem(item, toggle = false) {
      this.activeItem = item
      this.editForm = {
        name: item?.name || '',
        alt: item?.custom_properties?.alt || '',
        caption: item?.custom_properties?.caption || '',
      }
      if (toggle) {
        this.toggleSelection(item)
      }
    },

    isSelected(id) {
      return this.selectedIds.includes(id)
    },

    toggleSelection(item) {
      if (this.isSelected(item.id)) {
        this.selectedIds = this.selectedIds.filter(id => id !== item.id)
      } else {
        this.selectedIds = [...this.selectedIds, item.id]
      }
    },

    toggleSelectAllVisible() {
      if (this.allVisibleSelected) {
        const visibleIds = this.mediaItems.map(item => item.id)
        this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id))
      } else {
        const merged = new Set([...this.selectedIds, ...this.mediaItems.map(item => item.id)])
        this.selectedIds = Array.from(merged)
      }
    },

    clearSelection() {
      this.selectedIds = []
    },

    async bulkDeleteSelected() {
      if (!this.canBulkDelete) return

      const confirmed = window.confirm(`Delete ${this.selectedLibraryIds.length} selected library upload(s)?`)
      if (!confirmed) return

      this.isBulkDeleting = true
      let successCount = 0

      try {
        for (const id of this.selectedLibraryIds) {
          try {
            await deleteMedia(id)
            successCount += 1
          } catch (e) {
            // continue deleting others
          }
        }

        this.selectedIds = this.selectedIds.filter(id => !this.selectedLibraryIds.includes(id))
        this.notifySuccess(`${successCount} media item(s) deleted.`)
        await this.fetchLibrary()
      } catch (error) {
        this.notifyError(error, 'Bulk delete failed.')
      } finally {
        this.isBulkDeleting = false
      }
    },

    resetEditForm() {
      this.editForm = {
        name: '',
        alt: '',
        caption: '',
      }
    },

    isImage(item) {
      return (item?.mime_type || '').startsWith('image/')
    },

    humanMime(mimeType) {
      if (!mimeType) return 'Unknown'
      if (mimeType.startsWith('image/')) return 'Image'
      if (mimeType.startsWith('video/')) return 'Video'
      return mimeType
    },

    handleImageError(event) {
      event.target.src = 'data:image/svg+xml;charset=UTF-8,%3csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3e%3crect width="100%25" height="100%25" fill="%23f3f2f7"/%3e%3ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%236e6b7b" font-family="Arial" font-size="18"%3ePreview unavailable%3c/text%3e%3c/svg%3e'
    },

    handleFileInput(event) {
      const files = Array.from(event.target.files || [])
      this.appendUploadQueue(files)
      event.target.value = null
    },

    handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files || [])
      this.appendUploadQueue(files)
    },

    appendUploadQueue(files) {
      if (!files.length) return
      this.uploadQueue = [...this.uploadQueue, ...files]
    },

    clearUploadQueue() {
      this.uploadQueue = []
    },

    async uploadSelectedFiles() {
      if (!this.uploadQueue.length) return

      const formData = new FormData()
      this.uploadQueue.forEach(file => {
        formData.append('files[]', file)
      })

      this.isUploading = true

      try {
        await uploadMedia(formData)
        this.uploadQueue = []
        this.currentPage = 1
        this.source = 'all'
        this.collection = ''
        await this.fetchLibrary()
        this.notifySuccess('Media uploaded successfully.')
      } catch (error) {
        this.notifyError(error, 'Media upload failed.')
      } finally {
        this.isUploading = false
      }
    },

    async saveMetadata() {
      if (!this.activeItem) return

      this.isSaving = true

      try {
        const response = await updateMedia(this.activeItem.id, {
          name: this.editForm.name,
          alt: this.editForm.alt,
          caption: this.editForm.caption,
        })

        const updated = response?.data?.data || response?.data || null

        if (updated) {
          const index = this.mediaItems.findIndex(item => item.id === this.activeItem.id)
          if (index > -1) {
            this.$set(this.mediaItems, index, {
              ...this.mediaItems[index],
              ...updated,
            })
            this.activeItem = this.mediaItems[index]
          }
        }

        this.notifySuccess('Media details updated.')
      } catch (error) {
        this.notifyError(error, 'Could not update media.')
      } finally {
        this.isSaving = false
      }
    },

    async removeMedia() {
      if (!this.activeItem || !this.canDeleteActive) return

      const confirmed = window.confirm('Are you sure you want to delete this media item?')
      if (!confirmed) return

      this.isDeleting = true

      try {
        await deleteMedia(this.activeItem.id)
        this.selectedIds = this.selectedIds.filter(id => id !== this.activeItem.id)
        this.notifySuccess('Media deleted successfully.')
        await this.fetchLibrary()
      } catch (error) {
        this.notifyError(error, 'Could not delete media.')
      } finally {
        this.isDeleting = false
      }
    },

    async copyUrl(item) {
      const url = item?.original_url || item?.preview_url
      if (!url) return

      try {
        await navigator.clipboard.writeText(url)
        this.notifySuccess('Media URL copied.')
      } catch (error) {
        this.notifyError(error, 'Could not copy URL.')
      }
    },

    openOriginal(item) {
      const url = item?.original_url || item?.preview_url
      if (!url) return
      window.open(url, '_blank')
    },

    formatFileSize(size) {
      const bytes = Number(size || 0)
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
    },

    notifySuccess(message) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      }
    },

    notifyError(error, fallbackMessage) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        fallbackMessage

      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style scoped>
.media-library-page { width: 100%; }
.media-library-card {
  border-radius: 1.1rem;
  overflow: hidden;
  border: 1px solid #eceaf3;
  box-shadow: 0 14px 34px rgba(34, 41, 47, 0.05);
}
.media-library-header {
  padding: 1.5rem;
  border-bottom: 1px solid #f0eef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: linear-gradient(180deg, rgba(115, 103, 240, 0.04), rgba(255,255,255,0.88));
}
.media-library-hero {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}
.media-library-hero__icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(135deg, #7367f0, #9f8fff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(115, 103, 240, 0.28);
}
.media-library-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.media-btn {
  min-height: 44px;
  border-radius: 14px;
  font-weight: 700;
  padding: 0.68rem 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer !important;
  transition: all 0.22s ease;
  border-width: 1px;
  letter-spacing: 0.1px;
}
.media-btn:hover,
.media-btn:focus {
  transform: translateY(-1px);
}
.media-btn--primary {
  background: linear-gradient(135deg, #7367f0, #8f80ff) !important;
  border-color: #7367f0 !important;
  color: #fff !important;
  box-shadow: 0 12px 26px rgba(115, 103, 240, 0.30);
}
.media-btn--primary:hover,
.media-btn--primary:focus {
  background: linear-gradient(135deg, #6558ea, #8274ff) !important;
  border-color: #6558ea !important;
  color: #fff !important;
  box-shadow: 0 16px 30px rgba(115, 103, 240, 0.36);
}
.media-btn--ghost {
  background: linear-gradient(180deg, #ffffff, #f8f8fc) !important;
  border-color: #d9d4e8 !important;
  color: #5f5a6b !important;
  box-shadow: 0 8px 18px rgba(34, 41, 47, 0.06);
}
.media-btn--ghost:hover,
.media-btn--ghost:focus {
  background: #f5f4ff !important;
  border-color: #cfc9e4 !important;
  color: #4b465c !important;
  box-shadow: 0 12px 24px rgba(34, 41, 47, 0.08);
}
.media-btn--danger {
  background: linear-gradient(180deg, #fff5f5, #fff) !important;
  border-color: rgba(234, 84, 85, 0.38) !important;
  color: #ea5455 !important;
  box-shadow: 0 8px 18px rgba(234, 84, 85, 0.08);
}
.media-btn--danger:hover,
.media-btn--danger:focus {
  background: linear-gradient(180deg, #ffe9e9, #fff7f7) !important;
  border-color: rgba(234, 84, 85, 0.52) !important;
  color: #d93f40 !important;
  box-shadow: 0 12px 24px rgba(234, 84, 85, 0.14);
}
.media-toolbar-card {
  margin: 1rem 1.25rem 0;
  border: 1px solid #eceaf3;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 10px 28px rgba(34, 41, 47, 0.04);
}
.media-library-toolbar {
  padding: 1rem 1rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.media-library-toolbar__left,
.media-library-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.media-control >>> .custom-select,
.media-control >>> .form-control,
.media-control >>> .input-group-text {
  min-height: 44px;
  border-radius: 12px;
  border-color: #e5e3ec;
  background: #fff;
  box-shadow: none;
}
.media-control >>> .form-control:focus,
.media-control >>> .custom-select:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.12);
}
.media-control__prepend {
  background: #fff;
}
.media-search-box { min-width: 280px; }
.media-filter-select { min-width: 160px; }
.media-filter-select--wide { min-width: 220px; }
.media-summary-pill {
  min-height: 42px;
  padding: 0 0.95rem;
  border-radius: 999px;
  background: #f5f4ff;
  color: #5e50ee;
  display: flex;
  align-items: center;
  font-weight: 600;
}
.media-stat-chips {
  padding: 0 1rem 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}
.media-chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
}
.media-chip--primary { background: rgba(115,103,240,0.12); color: #5e50ee; }
.media-chip--secondary { background: rgba(130,134,139,0.12); color: #6e6b7b; }
.media-chip--info { background: rgba(0,207,232,0.12); color: #00a7bf; }
.media-chip--success { background: rgba(40,199,111,0.12); color: #1f9d57; }
.media-chip--warning { background: rgba(255,159,67,0.14); color: #d67e17; }
.media-bulk-bar {
  margin: 1rem 1.25rem 0;
  padding: 0.9rem 1rem;
  border: 1px solid #eceaf3;
  border-radius: 1rem;
  background: linear-gradient(180deg, #fff, #fcfcff);
  box-shadow: 0 10px 28px rgba(34, 41, 47, 0.04);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
}
.media-bulk-bar__left,
.media-bulk-bar__right {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
}
.media-upload-panel {
  margin: 1rem 1.25rem 0;
  padding: 1rem;
  border: 1px solid #eceaf3;
  border-radius: 1rem;
  background: linear-gradient(180deg, #fff, #fcfcff);
  box-shadow: 0 10px 28px rgba(34, 41, 47, 0.04);
}
.upload-queue-list {
  border: 1px solid #eceaf3;
  border-radius: 0.85rem;
  overflow: hidden;
  background: #fff;
}
.upload-queue-list__item {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #f2f1f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.upload-queue-list__item:last-child { border-bottom: none; }
.upload-queue-list__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f5f4ff;
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.6rem;
}
.media-library-content {
  display: flex;
  min-height: 72vh;
}
.media-library-main {
  flex: 1;
  min-width: 0;
  padding: 1.25rem;
  border-right: 1px solid #f0eef5;
}
.media-library-sidebar {
  width: 360px;
  background: linear-gradient(180deg, #fbfbfe, #f7f7fb);
  padding: 1.25rem;
}
.media-dropzone {
  border: 2px dashed rgba(115, 103, 240, 0.5);
  border-radius: 1rem;
  background: linear-gradient(180deg, rgba(115,103,240,0.05), rgba(255,255,255,0.95));
  transition: 0.2s ease;
}
.media-dropzone.is-dragging {
  background: rgba(115, 103, 240, 0.1);
  border-color: #5e50ee;
  transform: translateY(-1px);
}
.media-dropzone__inner {
  text-align: center;
  padding: 1.4rem;
}
.media-dropzone__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #f5f4ff;
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.55rem;
}
.media-state {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.media-empty-icon {
  width: 68px;
  height: 68px;
  border-radius: 18px;
  background: #f5f4ff;
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(165px, 1fr));
  gap: 1rem;
}
.media-grid__item {
  background: #fff;
  border: 1px solid #eceaf3;
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  transition: 0.22s ease;
  box-shadow: 0 8px 22px rgba(34, 41, 47, 0.03);
}
.media-grid__item:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(34, 41, 47, 0.08);
}
.media-grid__item.is-active {
  border-color: #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12), 0 14px 28px rgba(34, 41, 47, 0.06);
}
.media-grid__item.is-selected {
  border-color: #8d7fff;
  box-shadow: 0 0 0 3px rgba(141, 127, 255, 0.14), 0 14px 28px rgba(34, 41, 47, 0.06);
}
.media-grid__thumb {
  position: relative;
  height: 145px;
  background: linear-gradient(180deg, #fafaff, #f3f2f7);
  overflow: hidden;
}
.media-grid__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.media-grid__fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f2f7;
  color: #6e6b7b;
}
.media-grid__badge {
  position: absolute;
  left: 10px;
  bottom: 10px;
  background: rgba(34, 41, 47, 0.78);
  color: #fff;
  font-size: 0.68rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  backdrop-filter: blur(5px);
}
.media-grid__checkbox {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}
.media-grid__checkbox input {
  display: none;
}
.media-grid__checkbox span {
  width: 22px;
  height: 22px;
  display: block;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.95);
  background: rgba(34, 41, 47, 0.25);
  box-shadow: 0 4px 12px rgba(34, 41, 47, 0.18);
}
.media-grid__checkbox input:checked + span {
  background: #7367f0;
  border-color: #fff;
}
.media-grid__meta { padding: 0.85rem; }
.media-grid__name {
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.25;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.media-grid__sub {
  font-size: 0.78rem;
  color: #6e6b7b;
  margin-top: 0.3rem;
}
.media-pagination { margin-top: 1.5rem; }
.media-sidebar-card {
  border-radius: 1rem;
  overflow: hidden;
  position: sticky;
  top: 1rem;
  border: 1px solid #eceaf3;
  box-shadow: 0 12px 28px rgba(34, 41, 47, 0.05);
}
.media-sidebar-card__header {
  padding: 1rem 1rem 0.8rem;
  border-bottom: 1px solid #f0eef5;
  background: linear-gradient(180deg, rgba(115,103,240,0.05), #fff);
}
.media-sidebar-card__body { padding: 1rem; }
.media-sidebar-preview {
  width: 100%;
  height: 210px;
  border: 1px solid #eceaf3;
  border-radius: 1rem;
  overflow: hidden;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(115,103,240,0.03);
}
.media-sidebar-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}
.media-sidebar-preview__fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.media-sidebar-meta li {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.78rem;
  word-break: break-word;
}
.media-sidebar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.min-width-0 { min-width: 0; }

.media-grid__item,
.media-grid__item *,
.media-sidebar-actions .btn,
.media-library-actions .btn,
.media-upload-panel .btn,
.media-pagination .page-link,
.media-library-toolbar .custom-select,
.media-library-toolbar .form-control,
.media-library-toolbar .input-group-text {
  cursor: pointer !important;
}

@media (max-width: 991.98px) {
  .media-library-content { flex-direction: column; }
  .media-library-main { border-right: 0; border-bottom: 1px solid #f0eef5; }
  .media-library-sidebar { width: 100%; }
  .media-grid { grid-template-columns: repeat(auto-fill, minmax(135px, 1fr)); }
  .media-search-box { min-width: 100%; }
  .media-library-actions { width: 100%; }
  .media-btn { width: 100%; }
}

/* ==== Inline filter toolbar polish (functionality untouched) ==== */
.media-library-toolbar {
  align-items: center;
}

.media-library-toolbar__left--inline {
  display: grid !important;
  grid-template-columns: minmax(260px, 1.5fr) repeat(4, minmax(150px, 0.7fr));
  gap: 0.75rem;
  flex: 1 1 auto;
  width: 100%;
}

.media-library-toolbar__right--inline {
  flex: 0 0 auto;
  margin-left: auto;
}

.media-search-box {
  min-width: 0 !important;
  width: 100%;
}

.media-filter-select {
  min-width: 0 !important;
  width: 100%;
}

.media-filter-select--wide {
  min-width: 0 !important;
  width: 100%;
}

.media-toolbar-card {
  overflow: hidden;
}

.media-stat-chips {
  border-top: 1px solid #f0eef5;
  margin-top: 0.15rem;
  padding-top: 0.85rem;
}

@media (max-width: 1399.98px) {
  .media-library-toolbar__left--inline {
    grid-template-columns: minmax(240px, 1.4fr) repeat(4, minmax(135px, 0.7fr));
  }
}

@media (max-width: 1199.98px) {
  .media-library-toolbar__left--inline {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  .media-library-toolbar__right--inline {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
}

@media (max-width: 767.98px) {
  .media-library-toolbar__left--inline {
    grid-template-columns: 1fr;
  }

  .media-library-toolbar {
    align-items: stretch;
  }

  .media-library-toolbar__right--inline {
    width: 100%;
  }

  .media-summary-pill {
    width: 100%;
    justify-content: center;
  }
}


/* ==== Mobile attachment details UX improvement ==== */
.media-mobile-selection-bar,
.media-mobile-backdrop,
.media-mobile-drawer {
  display: none;
}

@media (max-width: 991.98px) {
  .media-mobile-selection-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.85rem;
    margin: 1rem 1.25rem 0;
    padding: 0.85rem 0.9rem;
    border: 1px solid #eceaf3;
    border-radius: 1rem;
    background: linear-gradient(180deg, #ffffff, #fbfbfe);
    box-shadow: 0 10px 24px rgba(34, 41, 47, 0.05);
  }

  .media-mobile-selection-bar__left {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1 1 auto;
  }

  .media-mobile-selection-bar__thumb {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    overflow: hidden;
    background: #f8f8fc;
    border: 1px solid #eceaf3;
    flex-shrink: 0;
  }

  .media-mobile-selection-bar__thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .media-mobile-selection-bar__thumb-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .media-mobile-selection-bar__meta {
    min-width: 0;
  }

  .media-mobile-selection-bar__name {
    font-weight: 700;
    font-size: 0.92rem;
    line-height: 1.2;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .media-mobile-selection-bar__button {
    width: auto;
    min-width: 96px;
    flex-shrink: 0;
  }

  .media-mobile-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(34, 41, 47, 0.36);
    z-index: 1040;
    backdrop-filter: blur(2px);
  }

  .media-mobile-drawer {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 84vh;
    background: #fff;
    border-top-left-radius: 1.25rem;
    border-top-right-radius: 1.25rem;
    box-shadow: 0 -16px 40px rgba(34, 41, 47, 0.16);
    z-index: 1050;
    transform: translateY(102%);
    transition: transform 0.24s ease;
    overflow: hidden;
  }

  .media-mobile-drawer.is-open {
    transform: translateY(0);
  }

  .media-mobile-drawer__handle {
    width: 52px;
    height: 5px;
    background: #d8d6de;
    border-radius: 999px;
    margin: 0.7rem auto 0.35rem;
  }

  .media-mobile-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.35rem 1rem 0.85rem;
    border-bottom: 1px solid #f0eef5;
  }

  .media-mobile-drawer__body {
    padding: 1rem;
    overflow-y: auto;
    max-height: calc(84vh - 56px);
  }

  .media-library-sidebar {
    display: none;
  }

  .media-library-main {
    border-right: 0 !important;
  }
}

</style>
